import Image from 'next/image';

interface AvatarWithFallbackProperties {
  alt: string;
  className?: string;
  fallbackText?: string;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  src?: string | null;
}

type AvatarSize = NonNullable<AvatarWithFallbackProperties['size']>;

function getAvatarSizeConfig(size: AvatarSize): { image: number; className: string; textClass: string } {
  if (size === 'xxs') {
    return { image: 24, className: 'w-6 h-6', textClass: 'text-xs' };
  }
  if (size === 'xs') {
    return { image: 32, className: 'w-8 h-8', textClass: 'text-sm' };
  }
  if (size === 'sm') {
    return { image: 40, className: 'w-10 h-10', textClass: 'text-base' };
  }
  if (size === 'md') {
    return { image: 48, className: 'w-12 h-12', textClass: 'text-lg' };
  }
  if (size === 'lg') {
    return { image: 64, className: 'w-16 h-16', textClass: 'text-xl' };
  }
  throw new Error(`Unsupported avatar size "${size as string}"`);
}

export function AvatarWithFallback({
  src,
  alt,
  size = 'md',
  fallbackText = '?',
  className = '',
}: AvatarWithFallbackProperties) {
  const config = getAvatarSizeConfig(size);

  // Skip placeholder URLs so we don't fire failing image requests against /api/placeholder/*
  if (src && !src.startsWith('/api/placeholder')) {
    return (
      <Image
        src={src.replace('_normal', '_200x200')}
        alt={alt}
        width={config.image}
        height={config.image}
        className={`${config.className} rounded-full hover:opacity-90 hover:scale-105 transition-transform ${className}`}
        loading="lazy"
        referrerPolicy="no-referrer"
        unoptimized
      />
    );
  }

  return (
    <div
      className={`${config.className} rounded-full bg-muted flex items-center justify-center hover:opacity-90 ${className}`}
    >
      <span className={`${config.textClass} font-semibold`}>{fallbackText}</span>
    </div>
  );
}
